import {FileExcelOutlined, PlusOutlined} from "@ant-design/icons";
import {Button, Divider} from "antd";
import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import AppContext from "../../context/app/context";
import DriverClassificationContext from "../../context/driver-classification/context";
import {useIsMount} from "../../utils/hooks/is-mount";
import styles from "./style.module.scss";
import DriverClassificationContextProvider from "../../context/driver-classification/provider";
import DriverClassificationTable from "../../components/driver-classification/driver-classification-table";
import DriverClassificationFilter from "../../components/driver-classification/driver-classification-filter";
import SearchFilterCard from "../../components/general/filter-card";
import Can from "../../utils/rbac/can";
import PageHeader from "../../components/general/layout/page-header";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import {Permissions} from "../../utils/Permissions/Permissions";
import ExportToExcelModal from "../../components/general/excel/export-to-excel-modal";
import TemplateExportExcel from "../../components/general/excel/expor-excel";
import {
    IExportDriverClassificationExcel,
    columnsExportDriverClassificationExcel,
} from "../../models/driver-classification/request";

interface IProps {
    children?: React.ReactNode;
}

const DriverClassificationComponent: React.FC<IProps> = (props) => {
    const {t} = useTranslation();
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const {direction} = useContext(AppContext);
    const navigate = useNavigate();

    const [search, setSearch] = useState<string | undefined>(undefined);

    const {actions, loading, query} = useContext(DriverClassificationContext);

    useEffect(() => {
        actions.setSearch(search);
    }, [search]);

    const isMounted = useIsMount();
    useEffect(() => {
        !isMounted && actions.getData();
    }, [query, direction]);

    return (
        <div dir={direction} className={styles.container}>
            <PageHeader
                title={t("driver_classifications")}
                subTitle={t("driver_classifications_management")}
                extra={[
                    <RefreshBtn
                        key={0}
                        loading={loading.includes("list")}
                        onClick={() => actions.getData()}
                    >
                        {t("refresh")}
                    </RefreshBtn>,
                    <Can key={2} perform={Permissions.CAN_CREATE_DRIVER_CLASSIFICATION}>
                        <Divider key={1} type="vertical"/>
                        <Button
                            type="primary"
                            onClick={() => {
                                navigate(`/driver-classifications/create`);
                            }}
                            icon={<PlusOutlined/>}
                        >
                            {t("add_driver_classification")}
                        </Button>
                    </Can>,
                    <Divider key={3} type="vertical"/>,
                    <Button
                        key={4}
                        type="primary"
                        onClick={() => {
                            setExportModalVisible(true);
                        }}
                        icon={<FileExcelOutlined/>}
                    >
                        {t("export_to_excel")}
                    </Button>,
                ]}
            />
            <SearchFilterCard
                formId="driver-classification-filter"
                searchValue={search}
                setSearchValue={setSearch}
                onReset={() => {
                    actions.setQuery({
                        page: query?.page,
                        perPage: query?.perPage,
                    });
                    const form = document.getElementById("driver-classification-filter");
                    if (form) {
                        form.dispatchEvent(
                            new Event("reset", {cancelable: true, bubbles: true})
                        );
                    }
                }}
                resetLoading={loading.includes("list")}
                applyLoading={loading.includes("list")}
            >
                <DriverClassificationFilter/>
            </SearchFilterCard>

            <DriverClassificationTable/>
            <ExportToExcelModal
                id="export-driver-classification"
                title={t("export_to_excel")}
                visible={exportModalVisible}
                toggleVisible={setExportModalVisible}
                width="50vw"
                defaultValues={
                    {
                        id: false,
                        name: false,
                        description: false,
                        calculatingDeliveryCostPerKilometer: false,
                        deliveryCostPerKilometer: false,
                        orderDeliveryCost: false,
                        extraOrderFixedBonusEnabled: false,
                        extraOrderFixedBonusAmount: false,
                    } as IExportDriverClassificationExcel
                }
                loading={loading.includes("create")}
                onSubmit={async (columnsNames) => {
                    await actions.exportExcel(columnsNames);
                    setExportModalVisible(false);
                }}
            >
                <TemplateExportExcel
                    names={columnsExportDriverClassificationExcel}
                />
            </ExportToExcelModal>
        </div>
    );
};

const DriverClassificationPage = () => {
    return (
        <DriverClassificationContextProvider>
            <DriverClassificationComponent/>
        </DriverClassificationContextProvider>
    );
};

export default DriverClassificationPage;
